import { WeeklyScheduleState } from '@/sections/attacks/capabilities/components/WeeklySchedule';

export interface ScheduleValidationErrors {
  dateRange?: string;
  schedule?: string;
}

export interface ScheduleValidationResult {
  isValid: boolean;
  errors: ScheduleValidationErrors;
}

/**
 * Validates a schedule configuration
 *
 * Checks that:
 * - Start date is provided
 * - End date (if provided) is after start date
 * - At least one day is enabled for scheduled execution
 * - All enabled days have a time configured
 *
 * @param startDate - The schedule start date (YYYY-MM-DD format)
 * @param endDate - Optional end date (YYYY-MM-DD format)
 * @param schedule - The weekly schedule state with enabled days and times
 * @returns Validation result with isValid flag and any errors
 */
export function validateScheduleConfig(
  startDate: string,
  endDate: string | undefined,
  schedule: WeeklyScheduleState | null
): ScheduleValidationResult {
  const errors: ScheduleValidationErrors = {};

  // Validate start date is provided
  if (!startDate) {
    errors.dateRange = 'Start date is required';
    return { isValid: false, errors };
  }

  // Validate end date is after start date (if provided)
  if (endDate && endDate < startDate) {
    errors.dateRange = 'End date must be after start date';
    return { isValid: false, errors };
  }

  // Validate schedule configuration exists
  if (!schedule) {
    errors.schedule = 'Schedule configuration is required';
    return { isValid: false, errors };
  }

  // Validate at least one day is enabled
  const hasEnabledDays = Object.values(schedule).some(day => day.enabled);
  if (!hasEnabledDays) {
    errors.schedule = 'Select at least one day for scheduled execution';
    return { isValid: false, errors };
  }

  // Validate all enabled days have a time configured
  const allEnabledDaysHaveTime = Object.values(schedule).every(
    day => !day.enabled || (day.enabled && day.time)
  );
  if (!allEnabledDaysHaveTime) {
    errors.schedule = 'All selected days must have a time configured';
    return { isValid: false, errors };
  }

  return { isValid: true, errors: {} };
}
